import { formStructure } from "./constants";
import { type FormStructure, type ProcessedSection, type FieldSetting, type FieldLookup } from "./types";

/**
 * Generate field lookup from form structure
 */
export const fieldLookup: FieldLookup = formStructure.reduce((lookup, section) => {
  section.fields.forEach((field) => {
    lookup[field.id] = {
      section: section.id,
      ...(field.columnSpan ? { columnSpan: field.columnSpan } : {}),
    };
  });
  return lookup;
}, {} as FieldLookup);

/**
 * Processes field settings from the API and combines them with the form structure
 * to create the final form layout with visibility and requirement information.
 */
export function processBookingFieldSettings(structure: FormStructure, apiSettings: FieldSetting[]): ProcessedSection[] {
  // Create a map of field settings for easy lookup
  const settingsMap = new Map(apiSettings.map((setting) => [setting.field, setting]));

  return structure
    .map((section) => {
      const processedFields = section.fields.map((field) => {
        const setting = settingsMap.get(field.id);
        return {
          ...field,
          isRequired: setting?.isRequired ?? false,
          isVisible: setting?.isVisible ?? true,
        };
      });

      // Filter out invisible fields
      const visibleFields = processedFields.filter((field) => field.isVisible);

      return {
        id: section.id,
        title: section.title,
        fields: visibleFields,
      };
    })
    .filter((section) => section.fields.length > 0); // Remove sections with no visible fields
}

/**
 * Checks if there are any visible fields in the field settings
 */
export const hasVisibleFields = (settings: FieldSetting[]): boolean => {
  return settings.some((setting) => setting.isVisible);
};
